/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import ConciergeConversationDrawer from './ConciergeConversationDrawer';

const G   = '#1D9E75';
const GD  = '#0F6E56';
const INK = '#0F1A16';

const SERVICES_LABELS = {
  checkin:       'Check-in / Check-out',
  cleaning:      'Ménage & linge',
  maintenance:   'Maintenance',
  keys:          'Gestion des clés',
  guest_support: 'Support voyageurs',
  pricing:       'Optimisation tarifaire',
  photos:        'Photos & annonces',
  accounting:    'Comptabilité',
  full:          'Gestion complète',
};

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

async function geocodeCity(city) {
  try {
    const res = await fetch(
      'https://nominatim.openstreetmap.org/search?format=json&q=' + encodeURIComponent(city) + '&limit=1',
      { headers: { 'Accept-Language': 'fr' } }
    );
    const data = await res.json();
    if (data && data[0]) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch(e) {}
  return null;
}

export default function ConciergeHostTab({ user, hostProfile, listings, isMobile, onNavigate }) {
  const [concierges, setConcierges]             = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [search, setSearch]                     = useState('');
  const [selectedId, setSelectedId]             = useState(null);
  const [form, setForm]                         = useState({ name:'', email:'', answers:{} });
  const [sending, setSending]                   = useState(false);
  const [sentId, setSentId]                     = useState(null);
  const [sentRequestId, setSentRequestId]       = useState(null);
  const [openConversation, setOpenConversation] = useState(null);
  const [geoInfo, setGeoInfo]                   = useState(null);
  const [sentRequests, setSentRequests]         = useState({});
  const [confirmRetireId, setConfirmRetireId]   = useState(null);
  const [expandedBiens, setExpandedBiens]       = useState(null);

  useEffect(() => { loadConcierges(); }, []);

  const loadConcierges = async () => {
    setLoading(true);
    let hostLat = null, hostLng = null;
    const hostCity = listings?.[0]?.city || '';
    if (hostCity) {
      const coords = await geocodeCity(hostCity);
      if (coords) { hostLat = coords.lat; hostLng = coords.lng; setGeoInfo({ city: hostCity, found: true }); }
      else setGeoInfo({ city: hostCity, found: false });
    }

    const { data } = await supabase
      .from('host_profiles')
      .select('id, full_name, company_name, concierge_zone, concierge_radius_km, concierge_tarif_mode, concierge_tarif_pct, concierge_tarif_fixed, concierge_services, concierge_custom_services, concierge_description, concierge_questions, concierge_languages, concierge_lat, concierge_lng, concierge_photo_url')
      .eq('is_manager', true)
      .eq('concierge_available', true);

    let result = data || [];
    if (hostLat && hostLng) {
      result = result.filter(c => {
        if (!c.concierge_lat || !c.concierge_lng) return true;
        const dist = haversine(hostLat, hostLng, c.concierge_lat, c.concierge_lng);
        return dist <= (c.concierge_radius_km || 50);
      });
    }

    const ids = result.map(c => c.id);
    if (ids.length > 0) {
      const { data: listingsManaged } = await supabase
        .from('listings')
        .select('id, managed_by, title, city, photos, status')
        .in('managed_by', ids);

      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('rating_overall, listing_id')
        .not('rating_overall', 'is', null);

      result = result.map(c => {
        const biens = (listingsManaged || []).filter(l => l.managed_by === c.id);
        const managedIds = biens.map(l => l.id);
        const reviews = (reviewsData || []).filter(r => managedIds.includes(r.listing_id));
        const note = reviews.length > 0
          ? (reviews.reduce((sum, r) => sum + r.rating_overall, 0) / reviews.length).toFixed(1)
          : null;
        return { ...c, biens_havenly: biens, nb_biens_geres: biens.length, note_moyenne: note, nb_avis: reviews.length };
      });
    }

    if (user?.id) {
      const { data: existingRequests } = await supabase
        .from('concierge_requests')
        .select('id, concierge_id')
        .eq('host_id', user.id)
        .eq('deleted_by_host', false);
      const sentMap = {};
      (existingRequests || []).forEach(r => { sentMap[r.concierge_id] = r.id; });
      setSentRequests(sentMap);
    }

    setConcierges(result);
    setLoading(false);
  };

  const filtered = concierges.filter(c => {
    const q = search.toLowerCase();
    const name = (c.company_name || c.full_name || '').toLowerCase();
    const zone = (c.concierge_zone || '').toLowerCase();
    return !q || name.includes(q) || zone.includes(q);
  });

  const setAnswer = (question, value) => setForm(p => ({ ...p, answers: { ...p.answers, [question]: value } }));
  const selected = concierges.find(c => c.id === selectedId);

  const send = async () => {
    if (!selected || !form.name || !form.email) return;
    setSending(true);
    try {
      const answers = (selected.concierge_questions || []).map(q => ({ question: q, answer: form.answers[q] || '' }));
      const { data: inserted } = await supabase.from('concierge_requests').insert({
        concierge_id: selected.id,
        host_id:      user?.id || null,
        host_name:    form.name,
        host_email:   form.email,
        host_phone:   null,
        answers,
        status:       'pending',
      }).select().single();
      if (inserted) {
        setSentId(selected.id);
        setSentRequestId(inserted.id);
        setSentRequests(p => ({ ...p, [selected.id]: inserted.id }));
      }
      await fetch('/api/send-email', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ type:'concierge_contact', to:'contact@havenly.fr', data:{ hostName:form.name, hostEmail:form.email, hostPhone:null, answers, conciergeName: selected.company_name || selected.full_name } }) }).catch(()=>{});
      await fetch('/api/send-email', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ type:'concierge_contact_ack', to:form.email, data:{ hostName:form.name, conciergeName: selected.company_name || selected.full_name, conciergeZone: selected.concierge_zone } }) }).catch(()=>{});
      setSelectedId(null);
      setForm({ name:'', email:'', answers:{} });
    } catch(e) { console.error(e); }
    setSending(false);
  };

  const deleteRequest = async (conciergeId) => {
    const requestId = sentRequests[conciergeId];
    if (!requestId) return;
    await supabase.from('concierge_requests').update({ deleted_by_host: true }).eq('id', requestId);
    setSentRequests(p => { const n = {...p}; delete n[conciergeId]; return n; });
    setSentId(null); setSentRequestId(null); setConfirmRetireId(null);
  };

  const tarifLabel = (c) => {
    if (c.concierge_tarif_mode === 'percentage' && c.concierge_tarif_pct) return c.concierge_tarif_pct + '% des revenus HT';
    if (c.concierge_tarif_mode === 'fixed' && c.concierge_tarif_fixed) return c.concierge_tarif_fixed + ' EUR / mois';
    if (c.concierge_tarif_mode === 'alacarte') return 'À la carte — sur devis';
    return null;
  };

  const goToListing = async (listingId) => {
    const { data } = await supabase.from('listings').select('*').eq('id', listingId).single();
    if (data && onNavigate) onNavigate('listing', data);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div>
        <div style={{ fontSize:18, fontWeight:600, color:INK }}>Conciergeries disponibles</div>
        <div style={{ fontSize:13, color:'#6B7280', marginTop:2 }}>Trouvez une conciergerie partenaire Havenly pour gérer vos biens.</div>
      </div>

      {geoInfo && (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:10, background: geoInfo.found ? '#E8F5F0' : '#FDF0E6', border:'1px solid ' + (geoInfo.found ? '#A7E3CD' : '#E8A87C'), fontSize:12 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={geoInfo.found ? GD : '#C4784A'} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          {geoInfo.found
            ? <span style={{ color:GD }}>Filtré depuis <strong>{geoInfo.city}</strong></span>
            : <span style={{ color:'#C4784A' }}>Géolocalisation impossible — toutes les conciergeries affichées</span>
          }
          <button onClick={loadConcierges} style={{ marginLeft:'auto', fontSize:11, color:'#6B7280', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>Actualiser</button>
        </div>
      )}

      <div style={{ position:'relative' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }}>
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom ou zone..."
          style={{ width:'100%', padding:'10px 12px 10px 36px', border:'1px solid #EAECEF', borderRadius:10, fontSize:13, fontFamily:'var(--sans)', outline:'none', boxSizing:'border-box', background:'#fff' }}/>
      </div>

      {loading ? (
        <div style={{ padding:'3rem', textAlign:'center', fontSize:13, color:'#9CA3AF' }}>Recherche des conciergeries proches...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background:'#fff', border:'2px dashed #EAECEF', borderRadius:14, padding:'4rem', textAlign:'center' }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
          <div style={{ fontSize:14, fontWeight:500, color:INK, marginBottom:8 }}>Aucune conciergerie disponible</div>
          <div style={{ fontSize:13, color:'#9CA3AF' }}>{search ? "Essayez une autre recherche." : "Aucune conciergerie ne s'est encore inscrite dans votre zone."}</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {filtered.map((c) => {
            const name = c.company_name || c.full_name || 'Conciergerie';
            const isOpen = selectedId === c.id;
            const hasSent = !!sentRequests[c.id];
            const tarif = tarifLabel(c);
            const allServices = [...(c.concierge_services || []), ...(c.concierge_custom_services || [])];
            const biens = c.biens_havenly || [];
            const showBiens = expandedBiens === c.id;

            return (
              <div key={c.id} style={{ background:'#fff', border:'1.5px solid ' + (isOpen ? G : '#EAECEF'), borderRadius:14, overflow:'hidden' }}>

                {/* Header : photo + infos + bouton */}
                <div style={{ padding:'14px 14px 10px' }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>

                    {/* Photo */}
                    <div style={{ width:50, height:50, borderRadius:10, background:'#E8F5F0', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {c.concierge_photo_url
                        ? <img src={c.concierge_photo_url} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                        : <span style={{ fontSize:20, fontWeight:700, color:GD }}>{name[0]?.toUpperCase()}</span>
                      }
                    </div>

                    {/* Nom + badge + bouton */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6 }}>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:14, fontWeight:600, color:INK, marginBottom:3, wordBreak:'break-word' }}>{name}</div>
                          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:'#E8F5F0', color:GD, fontWeight:500 }}>Disponible</span>
                        </div>
                        {/* Bouton principal */}
                        <div style={{ flexShrink:0 }}>
                          {!hasSent && (
                            <button onClick={() => setSelectedId(isOpen ? null : c.id)}
                              style={{ padding:'7px 12px', background: isOpen ? '#F3F4F6' : G, color: isOpen ? INK : '#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'var(--sans)', whiteSpace:'nowrap' }}>
                              {isOpen ? 'Fermer' : 'Contacter'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Infos sous le header */}
                  <div style={{ marginTop:10 }}>
                    {c.concierge_zone && (
                      <div style={{ fontSize:12, color:'#6B7280', marginBottom:5 }}>📍 {c.concierge_zone} · rayon {c.concierge_radius_km} km</div>
                    )}
                    <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:8, marginBottom:4 }}>
                      {c.nb_biens_geres > 0 && (
                        <button onClick={() => setExpandedBiens(showBiens ? null : c.id)}
                          style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:G, fontWeight:500, background:'none', border:'none', cursor:'pointer', padding:0, fontFamily:'var(--sans)' }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={G} strokeWidth="2"><path d="M3 21V9l9-6 9 6v12"/></svg>
                          {c.nb_biens_geres} bien{c.nb_biens_geres > 1 ? 's' : ''} géré{c.nb_biens_geres > 1 ? 's' : ''} sur Havenly
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={G} strokeWidth="2.5" style={{ transform: showBiens ? 'rotate(180deg)' : 'none', transition:'transform .2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                      )}
                      {c.note_moyenne && (
                        <span style={{ fontSize:12, color:'#C4784A', display:'flex', alignItems:'center', gap:3, fontWeight:500 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="#C4784A" stroke="#C4784A" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                          {c.note_moyenne} ({c.nb_avis} avis)
                        </span>
                      )}
                    </div>
                    {tarif && <div style={{ fontSize:12, color:'#C4784A', fontWeight:500, marginBottom:4 }}>{tarif}</div>}
                    {c.concierge_languages && c.concierge_languages.length > 0 && (
                      <div style={{ fontSize:11, color:'#6B7280', marginBottom:5 }}>🌐 {c.concierge_languages.slice(0,3).join(', ')}{c.concierge_languages.length > 3 ? '...' : ''}</div>
                    )}
                    {allServices.length > 0 && (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                        {allServices.slice(0,4).map(s => (
                          <span key={s} style={{ fontSize:10, padding:'2px 7px', borderRadius:10, background:'#F3F4F6', color:'#6B7280' }}>{SERVICES_LABELS[s] || s}</span>
                        ))}
                        {allServices.length > 4 && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, background:'#F3F4F6', color:'#9CA3AF' }}>+{allServices.length - 4}</span>}
                      </div>
                    )}
                  </div>

                  {/* Boutons demande envoyée */}
                  {hasSent && (
                    <div style={{ marginTop:10, display:'flex', flexWrap:'wrap', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:12, color:GD, fontWeight:600 }}>Demande envoyée ✓</span>
                      <button onClick={() => setOpenConversation({ id: sentRequests[c.id], host_name: 'Moi', concierge_name: name })}
                        style={{ padding:'6px 10px', background:'#E8F5F0', color:GD, border:'1px solid #A7E3CD', borderRadius:8, fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'var(--sans)', display:'flex', alignItems:'center', gap:4 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                        Messagerie
                      </button>
                      {confirmRetireId === c.id ? (
                        <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                          <span style={{ fontSize:11, color:'#6B7280' }}>Retirer ?</span>
                          <button onClick={() => deleteRequest(c.id)} style={{ padding:'3px 8px', background:'#FCEBEB', color:'#A32D2D', border:'1px solid #F5C6C6', borderRadius:5, fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'var(--sans)' }}>Oui</button>
                          <button onClick={() => setConfirmRetireId(null)} style={{ padding:'3px 8px', background:'#F3F4F6', color:'#6B7280', border:'none', borderRadius:5, fontSize:11, cursor:'pointer', fontFamily:'var(--sans)' }}>Non</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmRetireId(c.id)}
                          style={{ padding:'5px 10px', background:'none', border:'1px solid #EAECEF', borderRadius:8, fontSize:11, color:'#9CA3AF', cursor:'pointer', fontFamily:'var(--sans)' }}>
                          Retirer
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Description */}
                {c.concierge_description && (
                  <div style={{ padding:'0 14px 12px', fontSize:12, color:'#6B7280', lineHeight:1.6 }}>{c.concierge_description}</div>
                )}

                {/* Biens Havenly */}
                {showBiens && biens.length > 0 && (
                  <div style={{ borderTop:'1px solid #F3F4F6', padding:'12px 14px', background:'#FAFAFA' }}>
                    <div style={{ fontSize:12, fontWeight:600, color:INK, marginBottom:8 }}>Biens gérés sur Havenly</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {biens.map(b => (
                        <div key={b.id} onClick={() => goToListing(b.id)}
                          style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', background:'#fff', border:'1px solid #EAECEF', borderRadius:10, cursor:'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = G}
                          onMouseLeave={e => e.currentTarget.style.borderColor = '#EAECEF'}>
                          <div style={{ width:40, height:40, borderRadius:8, overflow:'hidden', flexShrink:0, background:'#F3F4F6', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {b.photos?.[0]
                              ? <img src={b.photos[0]} alt={b.title} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5"><path d="M3 21V9l9-6 9 6v12"/></svg>
                            }
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:12, fontWeight:600, color:INK, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.title}</div>
                            {b.city && <div style={{ fontSize:11, color:'#9CA3AF' }}>{b.city}</div>}
                          </div>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Formulaire contact */}
                {isOpen && !hasSent && (
                  <div style={{ borderTop:'1px solid #EAECEF', padding:'16px 14px', background:'#F9FAFB' }}>
                    <div style={{ fontSize:13, fontWeight:600, color:INK, marginBottom:4 }}>Envoyer une demande à {name}</div>
                    <div style={{ fontSize:11, color:'#9CA3AF', marginBottom:12, display:'flex', alignItems:'center', gap:4 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      Ne partagez pas vos coordonnées avant la signature du contrat.
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:12 }}>
                      <div>
                        <div style={{ fontSize:11, color:'#6B7280', marginBottom:4, fontWeight:500 }}>Votre nom *</div>
                        <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Jean Dupont"
                          style={{ width:'100%', padding:'9px 10px', border:'1px solid #EAECEF', borderRadius:8, fontSize:13, fontFamily:'var(--sans)', outline:'none', boxSizing:'border-box', background:'#fff' }}/>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:'#6B7280', marginBottom:4, fontWeight:500 }}>Email *</div>
                        <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jean@email.fr"
                          style={{ width:'100%', padding:'9px 10px', border:'1px solid #EAECEF', borderRadius:8, fontSize:13, fontFamily:'var(--sans)', outline:'none', boxSizing:'border-box', background:'#fff' }}/>
                      </div>
                    </div>
                    {(c.concierge_questions || []).length > 0 && (
                      <div style={{ marginBottom:12 }}>
                        <div style={{ fontSize:12, color:'#6B7280', marginBottom:8, fontWeight:500 }}>Questions de la conciergerie</div>
                        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                          {c.concierge_questions.map((q, qi) => (
                            <div key={qi}>
                              <div style={{ fontSize:12, color:INK, marginBottom:4, fontWeight:500 }}>{q}</div>
                              <input value={form.answers[q] || ''} onChange={e => setAnswer(q, e.target.value)} placeholder="Votre réponse..."
                                style={{ width:'100%', padding:'8px 10px', border:'1px solid #EAECEF', borderRadius:8, fontSize:13, fontFamily:'var(--sans)', outline:'none', boxSizing:'border-box', background:'#fff' }}/>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <button onClick={send} disabled={sending || !form.name || !form.email}
                      style={{ width:'100%', padding:'11px', background: !form.name || !form.email ? '#EAECEF' : G, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor: !form.name || !form.email ? 'default' : 'pointer', fontFamily:'var(--sans)' }}>
                      {sending ? 'Envoi...' : 'Envoyer ma demande'}
                    </button>
                    <div style={{ fontSize:11, color:'#9CA3AF', textAlign:'center', marginTop:8 }}>La conciergerie vous répondra via la messagerie Havenly</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {openConversation && (
        <ConciergeConversationDrawer
          request={openConversation}
          currentUserId={user?.id}
          isManager={false}
          onClose={() => setOpenConversation(null)}
        />
      )}
    </div>
  );
}
